import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import QuestionsSidebar from '../components/QuestionsSidebar';
import DiscussionView from '../components/questions/DiscussionView';
import AnswerQuestionModal from '../components/shared/AnswerQuestionModal';
import FloatingActionMenu from '../components/shared/FloatingActionMenu';
import { useQuestionsWebSocket } from '../hooks/useQuestionsWebSocket';
import { useKnowledgeData } from '../hooks/useKnowledgeData';
import { api } from '../lib/api-client';
import toast from 'react-hot-toast';

export default function Questions() {
  const navigate = useNavigate();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(() => window.innerWidth <= 980);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProduct, setSelectedProduct] = useState('all');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [sidebarWidth, setSidebarWidth] = useState(128); // Track sidebar width dynamically
  const [isAnswerModalOpen, setIsAnswerModalOpen] = useState(false);
  const [questionToAnswer, setQuestionToAnswer] = useState(null);

  // State for real data from API
  const [questions, setQuestions] = useState([]);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(true);
  const [isLoadingProducts, setIsLoadingProducts] = useState(true);
  const [isLoadingCategories, setIsLoadingCategories] = useState(true);

  // Knowledge data for FloatingActionMenu
  const { collections, products: knowledgeProducts, fetchCollections, fetchProducts: fetchKnowledgeProducts } = useKnowledgeData({
    fetchOnMount: false
  });

  // Update window width on resize
  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch questions from API
  const fetchQuestions = useCallback(async () => {
    setIsLoadingQuestions(true);
    try {
      const data = await api.get('/api/v1/questions');
      setQuestions(data);
    } catch (error) {
      console.error('Failed to fetch questions:', error);
      toast.error('Failed to load questions');
    } finally {
      setIsLoadingQuestions(false);
    }
  }, []);

  // Fetch products from API
  const fetchProducts = useCallback(async () => {
    setIsLoadingProducts(true);
    try {
      const data = await api.get('/api/v1/questions/products');
      setProducts(data);
    } catch (error) {
      console.error('Failed to fetch products:', error);
      toast.error('Failed to load products');
    } finally {
      setIsLoadingProducts(false);
    }
  }, []);

  // Fetch categories from API
  const fetchCategories = useCallback(async () => {
    setIsLoadingCategories(true);
    try {
      const data = await api.get('/api/v1/questions/categories');
      setCategories(data);
    } catch (error) {
      console.error('Failed to fetch categories:', error);
      toast.error('Failed to load categories');
    } finally {
      setIsLoadingCategories(false);
    }
  }, []);

  // Load data on mount
  useEffect(() => {
    fetchQuestions();
    fetchProducts();
    fetchCategories();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Sidebar configuration - wider sidebar with responsive mode
  const sidebarMode = windowWidth <= 980 ? 'overlay' : 'push';
  const sidebarWidthPx = sidebarWidth * 4;

  // Handle sidebar width change from resize
  const handleSidebarWidthChange = (newWidth) => {
    setSidebarWidth(newWidth);
  };

  // Filter questions based on search term, product, category, and answered status
  const filteredQuestions = (questions || []).filter(question => {
    // Handle null titles safely
    const title = question.title || '';
    const matchesSearch = title.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesProduct = selectedProduct === 'all' || question.product_id === selectedProduct;
    const matchesCategory = selectedCategory === 'all' || question.category === selectedCategory;
    const isNotAnswered = !question.is_answered; // Only show unanswered questions
    return matchesSearch && matchesProduct && matchesCategory && isNotAnswered;
  });

  const handleQuestionClick = (question) => {
    setSelectedQuestion(question);
  };

  const handleDiscussionMessageAdded = (updatedQuestion) => {
    // Update the selected question
    setSelectedQuestion(updatedQuestion);

    // Update the question in the list
    setQuestions(prevQuestions =>
      prevQuestions.map(q => q.id === updatedQuestion.id ? updatedQuestion : q)
    );
  };

  const handleAnswerClick = (question) => {
    setQuestionToAnswer(question);
    setIsAnswerModalOpen(true);
  };

  const handleAnswerSubmitted = (questionId, answer, metadata) => {
    console.log('Answer submitted for question:', questionId);
    console.log('Answer:', answer);
    console.log('Product ID:', metadata.productId);
    console.log('Category:', metadata.category);

    // Refetch questions to update the list
    fetchQuestions();

    // Close the modal
    setIsAnswerModalOpen(false);
    setQuestionToAnswer(null);
  };

  // WebSocket handlers for real-time updates
  const handleQuestionUpdated = useCallback((questionId, questionData) => {
    console.log('Question updated:', questionId, questionData);

    // Refetch questions to update the list
    fetchQuestions();

    // Show toast notification
    toast.success(`Question "${questionData.title}" was updated`, {
      duration: 2000,
      position: 'bottom-right'
    });
  }, [fetchQuestions]);

  const handleQuestionAnswered = useCallback((questionId, answerData) => {
    console.log('Question answered:', questionId, answerData);

    // Refetch questions to update the list
    fetchQuestions();

    // Show toast notification
    toast.success('A question has been answered', {
      duration: 2000,
      position: 'bottom-right'
    });
  }, [fetchQuestions]);

  // Initialize WebSocket connection for real-time updates
  const { isConnected, error } = useQuestionsWebSocket({
    onQuestionUpdated: handleQuestionUpdated,
    onQuestionAnswered: handleQuestionAnswered
  });

  return (
    <div className="h-screen w-screen overflow-hidden" style={{ background: 'linear-gradient(135deg, var(--color-gradient-from) 0%, var(--color-gradient-via) 50%, var(--color-gradient-to) 100%)' }}>
      {/* Grid pattern overlay */}
      <div className="absolute inset-0 pointer-events-none" style={{
        backgroundImage: 'linear-gradient(var(--color-border-subtle) 1px, transparent 1px), linear-gradient(90deg, var(--color-border-subtle) 1px, transparent 1px)',
        backgroundSize: '64px 64px',
        opacity: 0.3
      }}></div>

      {/* Animated background accents */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: 'var(--color-accent-primary)', opacity: 0.08 }}></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 rounded-full blur-3xl" style={{ backgroundColor: 'var(--color-info)', opacity: 0.08 }}></div>
      </div>

      {/* Sidebar */}
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        setIsCollapsed={setIsSidebarCollapsed}
        width={sidebarWidth}
        mode={sidebarMode}
        resizable={true}
        onWidthChange={handleSidebarWidthChange}
        minWidth={80}
        maxWidth={200}
        autoCollapseOnOverlay={true}
      >
        <QuestionsSidebar
          searchTerm={searchTerm}
          setSearchTerm={setSearchTerm}
          selectedProduct={selectedProduct}
          setSelectedProduct={setSelectedProduct}
          selectedCategory={selectedCategory}
          setSelectedCategory={setSelectedCategory}
          filteredQuestions={filteredQuestions}
          selectedQuestion={selectedQuestion}
          handleQuestionClick={handleQuestionClick}
          handleAnswerClick={handleAnswerClick}
          products={products}
          categories={categories}
        />
      </Sidebar>

      {/* Main Content - Responsive positioning */}
      <main
        className={`fixed top-0 right-0 bottom-0 overflow-hidden p-4 transition-all duration-300`}
        style={{
          left: sidebarMode === 'overlay' || isSidebarCollapsed ? '0' : `${sidebarWidthPx}px`
        }}
      >
        {/* Close Button - Top Right */}
        <button
          onClick={(e) => {
            e.stopPropagation();
            navigate('/home');
          }}
          className="absolute top-8 right-8 p-3 rounded-lg border transition-all duration-200 shadow-lg hover:shadow-xl z-10"
          style={{
            backgroundColor: 'var(--color-bg-elevated)',
            borderColor: 'var(--color-border-default)',
            color: 'var(--color-text-muted)'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-bg-hover)';
            e.currentTarget.style.borderColor = 'var(--color-border-strong)';
            e.currentTarget.style.color = 'var(--color-text-primary)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'var(--color-bg-elevated)';
            e.currentTarget.style.borderColor = 'var(--color-border-default)';
            e.currentTarget.style.color = 'var(--color-text-muted)';
          }}
          title="Close Questions"
        >
          <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>

        {/* Discussion View */}
        <div className="w-full h-full flex items-center justify-center">
          {isLoadingQuestions ? (
            <div className="flex flex-col items-center gap-4">
              <div className="w-12 h-12 border-4 border-t-transparent rounded-full animate-spin" style={{ borderColor: 'var(--color-accent-primary)', borderTopColor: 'transparent' }}></div>
              <p style={{ color: 'var(--color-text-muted)' }}>Loading questions...</p>
            </div>
          ) : (
            <DiscussionView
              question={selectedQuestion}
              onQuestionUpdated={handleDiscussionMessageAdded}
            />
          )}
        </div>
      </main>

      {/* Answer Question Modal */}
      <AnswerQuestionModal
        isOpen={isAnswerModalOpen}
        onClose={() => setIsAnswerModalOpen(false)}
        question={questionToAnswer}
        onAnswerSubmitted={handleAnswerSubmitted}
        products={products}
        categories={categories}
      />

      {/* WebSocket Connection Status Indicator */}
      {!isConnected && (
        <div className={`fixed bottom-4 right-4 px-4 py-2 ${error ? 'bg-red-900/90 border-red-700' : 'bg-yellow-900/90 border-yellow-700'} border rounded-lg shadow-lg z-50 flex items-center gap-2`}>
          <div className={`w-2 h-2 rounded-full ${error ? 'bg-red-500' : 'bg-yellow-500'} animate-pulse`} />
          <span className={`text-sm ${error ? 'text-red-100' : 'text-yellow-100'}`}>
            {error || 'Connecting...'}
          </span>
        </div>
      )}

      {/* Floating Action Menu */}
      <FloatingActionMenu
        collections={collections}
        products={knowledgeProducts}
        onRefreshProducts={fetchKnowledgeProducts}
        onRefreshCollections={fetchCollections}
      />
    </div>
  );
}
